import React, { useState } from "react";
import "./mobileInvoicesModal.css";
import api from "../../../api";

const formatINR = (value) => {
  if (value === null || value === undefined) return "-";
  const num = Number(value);
  const prefix = num < 0 ? "-₹" : "₹";
  return `${prefix}${Math.abs(num).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const INVOICE_COLUMNS = [
  { key: "Inv. No.",      label: "INV. NO.",     type: "text" },
  { key: "Inv. Dt",      label: "INV. DATE",    type: "date" },
  { key: "Inv. Amt",     label: "INV. AMT",     type: "number" },
  { key: "Over Due",     label: "OVER DUE",     type: "number" },
  { key: "Out Standing", label: "OUT STANDING", type: "number" },
  { key: "Days",         label: "DAYS",         type: "number" },
];

const sortInvoices = (rows, col, dir) => {
  if (!col) return rows;
  return [...rows].sort((a, b) => {
    const av = a[col];
    const bv = b[col];
    if (av == null) return 1;
    if (bv == null) return -1;
    let cmp = 0;
    if (col === "Inv. Dt") {
      cmp = new Date(av) - new Date(bv);
    } else if (typeof av === "number" && typeof bv === "number") {
      cmp = av - bv;
    } else {
      cmp = String(av).localeCompare(String(bv));
    }
    return dir === "asc" ? cmp : -cmp;
  });
};

export const MobileInvoiceModal = ({ invoices = [], partyName, partyId, onClose }) => {
  const [emailStatus, setEmailStatus] = useState(""); // "", "sending", "success", "error"
  const [emailMsg, setEmailMsg] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (key, type) => {
    if (sortCol === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(key);
      setSortDir(type === "number" ? "desc" : "asc");
    }
  };

  const sortIcon = (key) => {
    if (sortCol !== key) return "unfold_more";
    return sortDir === "asc" ? "arrow_upward" : "arrow_downward";
  };

  const sorted = sortInvoices(invoices, sortCol, sortDir);

  const handleEmailInvoices = async () => {
    if (!partyId) return;

    setShowConfirm(false);
    setEmailStatus("sending");
    setEmailMsg("");
    try {
      const response = await api.get(`/api/emailosinvoice?PartyId=${partyId}`);

      const errCode = response.data?.errCode;
      const errMsg  = response.data?.errMsg;

      if (errCode === -101 || errCode === -102) {
        setEmailStatus("error");
        setEmailMsg(errMsg || "Failed to send.");
        setTimeout(() => { setEmailStatus(""); setEmailMsg(""); }, 5000);
        return;
      }

      setEmailStatus("success");
      setEmailMsg(errMsg || "Email sent!");
      setTimeout(() => { setEmailStatus(""); setEmailMsg(""); }, 4000);
    } catch (error) {
      console.error("Email Invoices Error:", error.response?.data || error.message);
      setEmailStatus("error");
      setEmailMsg(error.response?.data?.errMsg || "Failed to send.");
      setTimeout(() => { setEmailStatus(""); setEmailMsg(""); }, 4000);
    }
  };

  return (
    <>
      <div className="mobileInvoiceModalOverlay">
        <div className="mobileInvoiceModalMain">

          {/* Header */}
          <div className="mobileInvoiceModalHeader">
            <p className="mobileInvoiceModalTitle">Outstanding Invoices</p>
            <span
              className="material-symbols-outlined mobileInvoiceModalCloseIcon"
              onClick={onClose}
            >
              close
            </span>
          </div>

          {/* Email button row */}
          <div className="mobileInvoiceEmailRow">
            {emailStatus === "success" && (
              <span className="mobileInvoiceEmailFeedback mobileInvoiceEmailSuccess">{emailMsg}</span>
            )}
            {emailStatus === "error" && (
              <span className="mobileInvoiceEmailFeedback mobileInvoiceEmailError">{emailMsg}</span>
            )}
            <button
              className="mobileInvoiceEmailBtn"
              onClick={() => setShowConfirm(true)}
              disabled={emailStatus === "sending"}
            >
              <span className="material-symbols-outlined">mail</span>
              {emailStatus === "sending" ? "Sending..." : "Email Invoices"}
            </button>
          </div>

          {/* Table with horizontal scroll */}
          <div className="mobileInvoiceTableWrapper">
            <table className="mobileInvoiceTable">
              <thead>
                <tr>
                  {INVOICE_COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className="mobileInvoiceSortTh"
                      onClick={() => handleSort(col.key, col.type)}
                    >
                      <div className="mobileInvoiceThInner">
                        {col.label}
                        <span className="material-symbols-outlined mobileInvoiceSortIcon">
                          {sortIcon(col.key)}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="mobileInvoiceEmpty">No outstanding invoices.</td>
                  </tr>
                ) : (
                  sorted.map((inv, idx) => {
                    const isOverdue = inv["Over Due"] > 0;
                    return (
                      <tr key={idx}>
                        <td className="mobileInvoiceInvNo">{inv["Inv. No."]}</td>
                        <td>
                          {inv["Inv. Dt"]
                            ? new Date(inv["Inv. Dt"]).toLocaleDateString("en-IN", {
                                day: "2-digit", month: "short", year: "numeric",
                              })
                            : "-"}
                        </td>
                        <td>{formatINR(inv["Inv. Amt"])}</td>
                        <td className={isOverdue ? "mobileInvoiceOverdue" : ""}>
                          {formatINR(inv["Over Due"])}
                        </td>
                        <td>{formatINR(inv["Out Standing"])}</td>
                        <td>{inv["Days"]}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Email Confirmation Popup */}
      {showConfirm && (
        <div className="mobileInvoiceConfirmOverlay">
          <div className="mobileInvoiceConfirmBox">
            <div className="mobileInvoiceConfirmIcon">
              <span className="material-symbols-outlined">mail</span>
            </div>
            <p className="mobileInvoiceConfirmTitle">Send Invoice Email?</p>
            <p className="mobileInvoiceConfirmMsg">
              Outstanding invoices for <strong>{partyName}</strong> will be emailed to the client.
            </p>
            <div className="mobileInvoiceConfirmBtns">
              <button className="mobileInvoiceConfirmCancel" onClick={() => setShowConfirm(false)}>
                Cancel
              </button>
              <button className="mobileInvoiceConfirmSend" onClick={handleEmailInvoices}>
                <span className="material-symbols-outlined">send</span>
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};